import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'

import { PROGRESS_COLORS } from '@src/util/constants'
import { Caption } from '@src/elements/typography/typography'

import './progress-bar-circle.sass'

// named explicitly, the class name is the svg element <circle>, which vue refuses as a component name
@Component({ name: 'ProgressCircle' })
class Circle extends Vue {

  @Prop({ required: true, default: 0 })
  progress: number

  @Prop({ required: false, default: 63 })
  diameter: number

  @Prop({ required: false, default: '' })
  image: string

  @Prop({ required: false, default: 4 })
  strokeWidth: number

  @Prop({ required: false, default: false })
  croppedProgress: boolean

  @Prop({ required: false, default: '#F3F3F3' })
  strokeColor: string

  @Prop({ required: false, default: '' })
  text: string

  getColor (progress, maxProgress) {
    const percentage = progress / maxProgress
    if (percentage <= 0.7) {
      return PROGRESS_COLORS.red
    } else if (0.7 < percentage && percentage <= 0.85) {
      return PROGRESS_COLORS.yellow
    } else {
      return PROGRESS_COLORS.cyan
    }
  }

  /**
   * Generate the circle used to progress bar circle
   *
   * @param {CreateElement} h
   * @returns {any}
   */
  render (h: CreateElement) {
    let progress = this.croppedProgress && this.progress > 75 ? 75 : this.progress
    const radius = this.diameter / 2
    const dasharray = Math.PI * (radius - this.strokeWidth) * 2
    const dashoffset = (100 - progress) / 100 * dasharray
    const dashoffsetCropped = (100 - 75) / 100 * dasharray
    const backgroundURL = this.image ? 'url(' + this.image + ')' : ''
    const backgroundSize = (this.diameter - 2 * this.strokeWidth) + 'px ' + (this.diameter - 2 * this.strokeWidth) + 'px'

    let multiLineText: string[] = []
    if (this.text !== '') {
      multiLineText = this.text.split(/[\s//]+/)
    }

    return <div class='progress-bar-circle-wrapper' style={{
      'height': (this.diameter) + 'px',
      'width': (this.diameter) + 'px',
      'background-image': backgroundURL,
      'background-position': 'center center',
      'background-repeat': 'no-repeat',
      'background-size': backgroundSize,
      'position': 'relative'
    }}>
      <svg class='progress-bar-circle' height={this.diameter} width={this.diameter}>
        <circle cx={radius} cy={radius} r={radius - this.strokeWidth} x='0' y='0' stroke={this.strokeColor}
                stroke-width={this.strokeWidth} fill='none'
                stroke-linecap='round' stroke-dashoffset={this.croppedProgress ? dashoffsetCropped : 0}
                stroke-dasharray={dasharray}>

        </circle>
        {progress ?
          <circle cx={radius} cy={radius} r={radius - this.strokeWidth} x='0' y='0'
                  stroke={this.croppedProgress ? this.getColor(progress, 75) : this.getColor(progress, 100)}
                  stroke-width={this.strokeWidth} fill='none'
                  stroke-linecap='round' stroke-dashoffset={dashoffset} stroke-dasharray={dasharray} /> : null}

      </svg>
      {multiLineText.length === 1 ?
        <Caption class='progress-circle-text-middle one-line'>{this.text}</Caption>
        :
        <Caption class='progress-circle-text-middle multiple-lines'>
          {multiLineText.map((text) => (
            <span>{text}</span>
          ))
          }
        </Caption>}
    </div>

  }
}

@Component
export class ProgressBarCircle extends Vue {

  @Prop({ required: true, default: 0 })
  progress: number

  @Prop({ required: false, default: 63 })
  diameter: number

  @Prop({ required: false, default: '' })
  image: string

  @Prop({ required: false, default: '' })
  text: string

  @Prop({ required: false, default: '' })
  footer: string

  @Prop({ required: false, default: false })
  croppedProgress: boolean

  @Prop({ required: false, default: 4 })
  strokeWidth: number

  @Prop({ required: false, default: '#F3F3F3' })
  strokeColor: string

  render (h: CreateElement) {
    // div will be taller to fit also the footer (the extra height is relative to the diameter of the circle: 25%)
    const heightCoefficient = this.diameter * 25 / 100
    const widthCoefficient = (this.diameter + 40) * 35 / 100

    return <div>
      {this.croppedProgress ?
        <Circle progress={Math.floor(this.progress * 75 / 100)} diameter={this.diameter} strokeWidth={this.strokeWidth} image={this.image}
                croppedProgress={this.croppedProgress} /> :
        <div style={{
          'height': (this.diameter + heightCoefficient) + 'px',
          'width': (this.diameter) + 'px',
          'margin-left': 'auto',
          'margin-right': 'auto',
          'padding-left': widthCoefficient / 2 + 'px',
          'padding-right': widthCoefficient / 2 + 'px'
        }}>
          <Circle progress={this.progress} diameter={this.diameter} strokeWidth={this.strokeWidth} image={this.image}
                  croppedProgress={this.croppedProgress} strokeColor={this.strokeColor} text={this.text}/>

          {this.footer !== '' ?
            <Caption class='progress-circle-text-footer'>{this.footer}</Caption> : ''
          }
        </div>
      }
    </div>
  }
}
